import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@ui/themed';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import type { FileListItem } from '../../../../components/ui/file-upload/types';
import { useHandlers } from '../../../../context/page-context';
import { validatePriceList } from '../../csv-validation';
import { PriceListCsvError } from '../../errors';
import { useBulkUploadCsv } from '../../hooks/use-bulk-upload-csv';
import { QK_ALL_PRICE_LISTS, QK_PAGINATED_PRICE_LISTS } from '../../hooks/use-price-lists';
import { useUploadCsv } from '../../hooks/use-upload-csv';
import type { PriceList } from '../../types';
import { ManualPricing } from '../builder/manual-pricing';

export function UploadCsvModal({
  priceList,
  isOpen,
  closeModal,
}: {
  // If no price list, it'll be treated as a bulk upload
  priceList: PriceList | null;
  isOpen: boolean;
  closeModal: () => void;
}) {
  const queryClient = useQueryClient();
  const { notificationHandler } = useHandlers();

  const { mutateAsync: uploadCsv } = useUploadCsv();
  const { mutateAsync: bulkUploadCsv } = useBulkUploadCsv();

  const isBulk = !priceList;

  const [file, setFile] = useState<FileListItem | undefined>();
  const [csvErrors, setCsvErrors] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);

  // Clear modal data when it opens
  // NOTE: we do this instead of on close to prevent the UI resetting as it's animating out
  useEffect(() => {
    if (isOpen) {
      setFile(undefined);
      setCsvErrors([]);
      setFileError(undefined);
      setLoading(false);
    }
  }, [isOpen]);

  // Using arrow function here to avoid hoisting
  const handleSubmit = async () => {
    setLoading(true);
    setCsvErrors([]);
    setFileError(undefined);

    if (!file) {
      setFileError(t('price_list_builder.notifications.errors.select_price_list'));
      setLoading(false);
      return;
    }

    try {
      const validationErrors = await validatePriceList(file.file, isBulk);
      if (validationErrors.length > 0) {
        throw new PriceListCsvError(validationErrors);
      }

      if (isBulk) {
        await bulkUploadCsv(file.file);
      } else {
        await uploadCsv({ slug: priceList.slug, file: file.file });
      }
    } catch (err) {
      if (err instanceof PriceListCsvError) {
        setCsvErrors(err.errors);
      }

      notificationHandler.error(t('price_list_builder.notifications.errors.price_list_csv'));
      setLoading(false);
      return;
    }

    notificationHandler.success(
      t(`price_lists.notifications.success.csv_upload_succeeded.${isBulk ? 'bulk' : 'single'}`),
    );
    queryClient.invalidateQueries({ queryKey: QK_PAGINATED_PRICE_LISTS() });
    queryClient.invalidateQueries({ queryKey: QK_ALL_PRICE_LISTS() });
    closeModal();

    setLoading(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      primaryAction={{
        text: t('buttons:continue'),
        onClick: handleSubmit,
        loading,
      }}
      secondaryAction={{
        text: t('buttons:cancel'),
        onClick: closeModal,
      }}
    >
      <Modal.Header>
        {t(`pages:price_lists.modals.upload_csv.title.${isBulk ? 'bulk' : 'single'}`)}
      </Modal.Header>
      <Modal.Body>
        <ManualPricing
          onSelectedFileChanged={(file) => {
            setFile(file);
            setFileError(undefined);
            setCsvErrors([]);
          }}
          selectedFile={file}
          fileError={fileError}
          csvErrors={csvErrors}
          priceListName={priceList?.name}
          isBulk={isBulk}
        />
      </Modal.Body>
    </Modal>
  );
}
